import { useRef, useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Paperclip, Loader2, X, Images, Check } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImage, getMediaList } from '@/lib/api';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// w optionnel : largeur max en px pour les images MongoDB (ex: 600 pour catalogue, 1200 pour fiche produit)
export const getImageUrl = (url, w = null) => {
  if (!url) return '';
  if (url.startsWith('/api/')) {
    const base = `${BACKEND_URL}${url}`;
    return w ? `${base}?w=${w}` : base;
  }
  return url;
};

function MediaPickerModal({ open, onClose, onSelect }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getMediaList();
      setImages(res.data);
    } catch {
      toast.error('Impossible de charger la médiathèque');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSelected(null);
      load();
    }
  }, [open, load]);

  const handleConfirm = () => {
    if (!selected) return;
    onSelect(`/api/images/${selected.id}`);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-4xl w-full" data-testid="media-picker-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Images className="w-5 h-5 text-primary" />
            Médiathèque — choisir une image
          </DialogTitle>
        </DialogHeader>

        {/* Grid */}
        <div className="overflow-y-auto max-h-[60vh] pr-1">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <Images className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Aucune image dans la médiathèque.</p>
              <p className="text-sm mt-1">Utilisez le trombone pour uploader d'abord une image.</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 p-1">
              {images.map((img) => {
                const isSelected = selected?.id === img.id;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => setSelected(img)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all focus:outline-none ${
                      isSelected
                        ? 'border-primary ring-2 ring-primary/30 scale-95'
                        : 'border-transparent hover:border-primary/40 hover:scale-[0.97]'
                    }`}
                    data-testid={`media-picker-item-${img.id}`}
                  >
                    <img
                      src={`${BACKEND_URL}/api/images/${img.id}`}
                      alt={img.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <span className="bg-primary text-primary-foreground rounded-full p-1">
                          <Check className="w-4 h-4" />
                        </span>
                      </div>
                    )}
                    <div className="absolute bottom-0 inset-x-0 bg-black/50 px-1 py-0.5">
                      <p className="text-white text-[10px] truncate">{img.filename}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} data-testid="media-picker-cancel">
            Annuler
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selected}
            data-testid="media-picker-confirm"
          >
            <Check className="w-4 h-4 mr-1" />
            Sélectionner
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const ImageUploadField = ({ label, value, onChange, fieldId, required = false }) => {
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadImage(formData);
      onChange(response.data.url);
      toast.success('Image téléchargée avec succès');
    } catch {
      toast.error("Erreur lors du téléchargement de l'image");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div>
      {label && <Label htmlFor={fieldId}>{label}{required && ' *'}</Label>}
      <div className="flex gap-2 mt-1">
        <Input
          id={fieldId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="https://example.com/image.jpg ou choisissez via les boutons →"
          className="flex-1"
          data-testid={`${fieldId}-url-input`}
        />

        {/* Upload depuis l'ordinateur */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          title="Télécharger depuis l'ordinateur"
          data-testid={`${fieldId}-upload-btn`}
        >
          {uploading
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Paperclip className="w-4 h-4" />
          }
        </Button>

        {/* Choisir depuis la médiathèque */}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={() => setPickerOpen(true)}
          title="Choisir depuis la médiathèque"
          data-testid={`${fieldId}-picker-btn`}
        >
          <Images className="w-4 h-4" />
        </Button>

        {/* Effacer */}
        {value && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => onChange('')}
            title="Supprimer l'image"
            data-testid={`${fieldId}-clear-btn`}
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </Button>
        )}

        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileUpload}
          data-testid={`${fieldId}-file-input`}
        />
      </div>

      {/* Aperçu */}
      {value && (
        <div className="mt-2 relative inline-block">
          <img
            src={getImageUrl(value)}
            alt="Aperçu"
            className="h-20 w-auto rounded border border-border object-cover"
          />
        </div>
      )}

      {/* Modal médiathèque */}
      <MediaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={onChange}
      />
    </div>
  );
};
